import type { FunctionComponent } from 'react'
import React, { useState } from 'react'
import { ButtonLink, TypographyV2 as Typography } from '@which/seatbelt'
import { TickBoldIcon } from '@which/seatbelt/src/components/Icons/Miscellaneous/TickBold'
import { ChevronDownIcon, ChevronUpIcon } from '@which/seatbelt/src/components/Icons/Navigational'
import { dynamicGa4DataLayerPush } from '@which/shared'

import { useFeatureIsOn } from '@growthbook/growthbook-react'

import { GridArticle } from '../../../../shared/grids'
import { useMatchMedia } from '../../../../shared/hooks/useMatchMedia'
import styles from './FreeMembershipArticleHeader.module.scss'

const benefits = [
  <>
    <strong>Buy the best</strong> by unlocking expert reviews you can trust for everyday purchases,
    small luxuries, travel experiences and more.
  </>,
  <>
    <strong>Save money</strong> in your daily life with the latest advice by Which? experts.
  </>,
  <>
    <strong>Get more out of Which?</strong> with insider access, providing samples of member-only
    reviews and perks like saving articles.
  </>,
]

const getValidUrl = (maybeValidUrl: string): URL | null => {
  try {
    return new URL(maybeValidUrl)
  } catch {
    return null
  }
}

type Props = {
  articleFreeAccountStatus?: string | null
  displayLocking: boolean
  articleUrl: string
}

export const FreeMembershipArticleHeader: FunctionComponent<Props> = ({
  articleFreeAccountStatus,
  displayLocking,
  articleUrl,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const isFeatureEnabled = useFeatureIsOn('acq-976-new-free-membership-banner') ?? false
  const isXLargeDesktopOrAbove = useMatchMedia('(min-width: 1536px)')

  const redirectUrl = getValidUrl(articleUrl) ?? getValidUrl(window?.location?.href) ?? new URL('/')

  if (
    !isFeatureEnabled ||
    !(articleFreeAccountStatus === 'lock' || articleFreeAccountStatus === 'lockContent') ||
    !displayLocking
  ) {
    return null
  }

  const handleBenefitsToggle = () => {
    const nextExpandedState = !isExpanded
    setIsExpanded(nextExpandedState)
    dynamicGa4DataLayerPush({
      event: 'click_accordion',
      item_text: 'Free membership benefits',
      action_group: nextExpandedState ? 'expand' : 'collapse',
    })
  }

  return (
    <section
      className={styles.banner}
      aria-labelledby="free-membership-header-title"
      data-testid="free-membership-article-header"
    >
      <GridArticle bypass={isXLargeDesktopOrAbove}>
        <div className={styles.content}>
          <div className={styles.primaryRow}>
            <div className={styles.message}>
              <Typography
                tag="h2"
                textStyle="sb-text-heading-medium"
                className={styles.heading}
                id="free-membership-header-title"
              >
                Unlock this page with a free membership
              </Typography>
              <Typography tag="p" textStyle="sb-text-interface-body-default-regular">
                Sign up for a <strong>free membership</strong> to read this article and get more
                benefits
              </Typography>
            </div>
            <div className={styles.actions}>
              <ButtonLink
                href={`/free-account?return_url=${encodeURIComponent(redirectUrl.pathname)}`}
                appearance="primary"
                className={styles.signup}
                data-item-tex="Sign up"
                data-action-group="free account signup"
                data-item-spec="locked article header"
                data-which-id="locked-article-header-fa-signup-button"
              >
                Sign up
              </ButtonLink>
            </div>
          </div>

          <button
            className={styles.benefitsToggle}
            type="button"
            aria-expanded={isExpanded}
            aria-controls="free-membership-benefits"
            onClick={handleBenefitsToggle}
            data-which-id="free-membership-benefits-toggle"
            data-action-group={isExpanded ? 'collapse' : 'expand'}
          >
            <span className={styles.benefitsLabel}>
              {isExpanded ? 'Hide benefits' : 'See benefits'}
            </span>
            <span className={styles.chevron} aria-hidden="true">
              {isExpanded ? (
                <ChevronUpIcon customColour="#000000" />
              ) : (
                <ChevronDownIcon customColour="#000000" />
              )}
            </span>
          </button>

          <ul className={styles.benefits} id="free-membership-benefits" hidden={!isExpanded}>
            {benefits.map((benefit, index) => (
              <li className={styles.benefit} key={index}>
                <span className={styles.benefitIcon} aria-hidden="true">
                  <TickBoldIcon />
                </span>
                <Typography textStyle="sb-text-body-default-regular">{benefit}</Typography>
              </li>
            ))}
          </ul>
        </div>
      </GridArticle>
    </section>
  )
}
